import { useState, useEffect, useCallback } from 'react';
import { services } from '../../api';

/**
 * Custom hook to manage the state and data fetching for a paginated list of courses.
 *
 * @param {number} [initialPage=1] - The initial page number to start fetching from.
 * @returns {{
 *   coursesList: any[],
 *   loading: boolean,
 *   hasMore: boolean,
 *   error: boolean,
 *   loadMore: () => void,
 *   refreshData: () => void
 * }} The state and functions to manage courses data.
 *
 * @example
 * const { coursesList, loading, hasMore, loadMore, refreshData } = useCourses();
 * if (loading) return <LoadingSpinner />;
 * return (
 *   <FlatList
 *     data={coursesList}
 *     onEndReached={loadMore}
 *     refreshing={loading}
 *     onRefresh={refreshData}
 *     renderItem={({ item }) => <CourseItem course={item} />}
 *   />
 * );
 */
export const useCourses = (
  initialPage: number = 1,
): {
  coursesList: any[];
  loading: boolean;
  hasMore: boolean;
  error: boolean;
  loadMore: () => void;
  refreshData: () => void;
} => {
  const [coursesList, setCoursesList] = useState<any[]>([]); // Store courses in an array
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(initialPage); // Current page
  const [hasMore, setHasMore] = useState(true); // Check if more data is available
  const [totalPages, setTotalPages] = useState<number>(1); // Track total number of pages
  const [error, setError] = useState(false); // Track errors

  /**
   * Fetches courses for a specific page from the API.
   *
   * @param {number} page - The page number to fetch.
   * @returns {Promise<void>}
   */
  const fetchCourses = useCallback(
    async (page: number) => {
      if (loading) {
        return;
      } // Prevent multiple requests while loading
      setLoading(true);
      setError(false); // Reset error state
      try {
        const response = await services.courses.getList(page);
        const { data } = response.content;
        const { total, per_page, current_page } = response.content;
        setCoursesList((prevData) => (prevData && page !== 1 ? [...prevData, ...data] : data));
        setTotalPages(Math.ceil(total / per_page));
        setHasMore(current_page < totalPages);
      } catch (error) {
        console.error('Error fetching courses:', error);
        setError(true); // Set error state on failure
      } finally {
        setLoading(false);
      }
    },
    [loading, totalPages],
  );

  /**
   * Triggers the `fetchCourses` function whenever the page changes.
   */
  useEffect(() => {
    fetchCourses(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  /**
   * Loads the next page of courses.
   * Prevents loading if there's no more data, an error occurred, or already loading.
   */
  const loadMore = () => {
    if (!hasMore || loading || error) {
      return;
    }
    setPage((prevPage) => prevPage + 1);
  };

  /**
   * Refreshes the courses list by resetting the page and fetching the first page.
   */
  const refreshData = () => {
    setCoursesList([]);
    setPage(1);
    fetchCourses(1);
  };

  return {
    coursesList,
    loading,
    hasMore,
    error,
    loadMore,
    refreshData,
  };
};
